import {
  ArrowLeftOutlined,
  CopyOutlined,
  GiftOutlined,
  InboxOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Divider, Input, message, QRCode, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import {
  createRechargeOrder,
  getRechargeOrder,
  getRechargePackages,
} from '@/services/pay';
import { getAccountPoints } from '@/services/points';
import { rechargeOrderStatusState } from '@/store/pay';
import type { CreateRechargeOrder, RechargePackage } from '@/types';

const DefaultInviteUrl = `${window.location.origin}/invite?shareCode=${window.config?.shareCode}`;
const WelcomeWords =
  '发现一个超好用的神器小灵面试，AI面试官24小时陪练，根据简历精准预测面试题并生成详细报告，跟我一起练面试、拿offer吧：';

export interface PointsGetterProps {
  className?: string;
  onPaySuccess?: () => void;
  /** 使用场景：modal 内或页面内，影响背景与圆角 */
  mode?: 'modal' | 'page';
}

/**
 * 积分充值与邀请好友组件
 * 适配 Modal（SelectPosition / StartInterviewFlowModal）与 Profile 页面
 */
const PointsGetter: React.FC<PointsGetterProps> = ({
  onPaySuccess,
  className = '',
  mode = 'modal',
}) => {
  const [currentPoints, setCurrentPoints] = useState<number>(0);
  const [loadingPoints, setLoadingPoints] = useState(true);

  const [rechargePackages, setRechargePackages] = useState<RechargePackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [currentPkg, setCurrentPkg] = useState<RechargePackage | null>(null);
  const [currentOrder, setCurrentOrder] = useState<CreateRechargeOrder | null>(null);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [qrStatus, setQrStatus] = useState<'loading' | 'active' | 'expired'>('loading');
  const [checkingStatus, setCheckingStatus] = useState(false);
  const rechargeOrderStatus = useRecoilValue(rechargeOrderStatusState);
  const intervalRef = useRef<number>();

  const loadPoints = async () => {
    try {
      setLoadingPoints(true);
      const pointsInfo = await getAccountPoints();
      setCurrentPoints(pointsInfo.balance);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('加载积分失败:', error);
    } finally {
      setLoadingPoints(false);
    }
  };

  useEffect(() => {
    loadPoints();
    void loadRechargePackages();
  }, []);

  const loadRechargePackages = async () => {
    try {
      setLoadingPackages(true);
      const list = await getRechargePackages();
      setRechargePackages(list);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('加载充值套餐失败:', error);
      message.error('加载充值套餐失败，请稍后重试');
    } finally {
      setLoadingPackages(false);
    }
  };

  useEffect(() => {
    if (!currentOrder) return;
    const status = rechargeOrderStatus[currentOrder.orderNo];
    if (status === 'PAID') {
      setCurrentOrder(null);
      void loadPoints();
      onPaySuccess?.();
    }
  }, [currentOrder, rechargeOrderStatus, onPaySuccess]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(WelcomeWords + DefaultInviteUrl);
      message.success('链接已复制到剪贴板');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = WelcomeWords + DefaultInviteUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('链接已复制到剪贴板');
      } catch {
        message.error('复制失败，请手动复制浏览器地址栏中的链接');
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  const handleSelectPackage = async (pkg: RechargePackage) => {
    if (creatingOrder) return;
    try {
      setCreatingOrder(true);
      setQrStatus('loading');
      const order = await createRechargeOrder(pkg.packageId);
      const expireTime = new Date(order.expireTime).getTime();
      clearInterval(intervalRef.current);
      intervalRef.current = window.setInterval(() => {
        if (expireTime < Date.now()) {
          setQrStatus('expired');
          clearInterval(intervalRef.current);
        }
      }, 2000);
      setCurrentOrder(order);
      setCurrentPkg(pkg);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('创建充值订单失败:', error);
      message.error('创建充值订单失败，请稍后重试');
    } finally {
      setCreatingOrder(false);
      setQrStatus('active');
    }
  };

  const handleCheckOrderStatus = async () => {
    if (!currentOrder) return;
    try {
      setCheckingStatus(true);
      const order = await getRechargeOrder(currentOrder.orderNo);
      if (order.status === 'PAID') {
        message.success('支付成功，积分已到账');
        setCurrentOrder(null);
        void loadPoints();
        onPaySuccess?.();
      } else if (order.status === 'PENDING') {
        message.info('订单未支付，请完成支付后再试');
      } else {
        message.error('订单已关闭或支付失败，请重新选择套餐发起支付');
        setCurrentOrder(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('查询支付状态失败:', error);
      message.error('查询支付状态失败，请稍后重试');
    } finally {
      setCheckingStatus(false);
    }
  };

  const isPageMode = mode === 'page';
  const containerClass = isPageMode
    ? `!bg-transparent ${className}`
    : `rounded-none ${className}`;

  return (
    <div
      className={`
        relative z-[1] p-4 sm:p-6
        bg-gradient-to-br from-[#fffbeb] to-[#fef3c7]
        ${!isPageMode ? 'rounded-lg' : ''}
        ${containerClass}
      `}
    >
      {/* 积分展示区 */}
      <div className="mb-4 text-center sm:mb-6">
        <span className="text-sm text-slate-600">您的当前积分</span>
        <div className="mt-2 text-3xl font-extrabold tracking-tight">
          {loadingPoints ? (
            <span className="inline-block h-9 w-24 animate-pulse rounded bg-slate-200" />
          ) : (
            <span className="bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">
              {currentPoints.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* 主内容区：响应式双列 / 单列 */}
      <div className="flex flex-col gap-4 md:flex-row md:gap-6">
        {/* 左侧：充值区 */}
        <div className="flex-1">
          <div className="mb-2">
            <span className="text-base font-bold text-slate-900">选择套餐</span>
            <span className="ml-1 text-base font-bold text-amber-600">微信扫码充值</span>
          </div>

          {!currentOrder && (
            <>
              {loadingPackages ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-36 min-h-[140px] animate-pulse rounded-2xl bg-white/60"
                    />
                  ))}
                </div>
              ) : rechargePackages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <InboxOutlined className="mb-3 text-4xl text-slate-300" />
                  <p className="text-slate-500">暂无可用充值套餐</p>
                  <Button type="link" onClick={() => void loadRechargePackages()}>
                    刷新重试
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  {creatingOrder && (
                    <div
                      className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm"
                      aria-busy="true"
                      aria-live="polite"
                    >
                      <Spin size="large" tip="正在生成订单..." />
                    </div>
                  )}
                  <div
                    className={`grid grid-cols-2 gap-3 sm:grid-cols-3 ${creatingOrder ? 'pointer-events-none opacity-60' : ''}`}
                  >
                    {rechargePackages.map((pkg) => (
                      <button
                        key={pkg.packageId}
                        type="button"
                        disabled={creatingOrder}
                        onClick={() => handleSelectPackage(pkg)}
                        className="
                          group flex min-h-[140px] cursor-pointer flex-col items-start justify-between
                          rounded-2xl border-2 border-dashed border-amber-300
                          bg-white/80 p-4 shadow-sm
                          transition-all duration-200 ease-out
                          hover:-translate-y-0.5 hover:border-amber-500 hover:bg-white/95
                          hover:shadow-lg hover:shadow-amber-500/15
                          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                          disabled:cursor-not-allowed
                        "
                      >
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xl font-extrabold bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">
                            {pkg.points}
                          </span>
                          <span className="text-xs text-slate-500">积分</span>
                        </div>
                        <div className="mt-2 flex w-full items-end justify-between gap-2">
                          <span className="text-2xl font-extrabold text-slate-900">
                            ¥{(pkg.price / 100).toFixed(2)}
                          </span>
                          {pkg.originalPrice > pkg.price && (
                            <span className="text-xs text-slate-400 line-through">
                              ¥{(pkg.originalPrice / 100).toFixed(2)}
                            </span>
                          )}
                        </div>
                        {pkg.description && (
                          <div className="mt-1 text-xs text-slate-500">{pkg.description}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {currentOrder && currentPkg && (
            <div
              className="flex flex-col items-center gap-3 rounded-2xl bg-white/60 p-4"
              role="region"
              aria-label="微信扫码支付"
            >
              <div className="qr-fade-in">
                <QRCode
                  value={currentOrder.codeUrl}
                  size={180}
                  className="!bg-white"
                  status={qrStatus}
                  onRefresh={() => handleSelectPackage(currentPkg)}
                />
              </div>
              <div className="text-center text-sm text-slate-600">
                <p>套餐：{currentPkg.points} 积分</p>
                <p>金额：¥{(currentPkg.price / 100).toFixed(2)}</p>
              </div>
              <div className="flex flex-col items-center gap-1 text-xs">
                <button
                  type="button"
                  onClick={() => setCurrentOrder(null)}
                  className="flex items-center gap-1 text-indigo-600 hover:text-indigo-500"
                >
                  <ArrowLeftOutlined />
                  选择其他套餐
                </button>
                <button
                  type="button"
                  onClick={handleCheckOrderStatus}
                  disabled={checkingStatus}
                  className="text-indigo-600 underline decoration-dashed hover:text-indigo-500 disabled:text-slate-400"
                >
                  {checkingStatus ? '查询中...' : '已完成支付？点击确认'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* 分割线：桌面端垂直，移动端水平 */}
        <Divider type="vertical" className="hidden !h-auto md:!block" />
        <Divider className="!my-2 md:!hidden" />

        {/* 右侧：邀请区 */}
        <div className="flex-1 space-y-4">
          <div className="flex items-center gap-2">
            <GiftOutlined className="text-lg text-amber-500" />
            <span className="text-base font-bold text-slate-900">邀请好友，额外拿能量</span>
          </div>

          <div className="flex items-start gap-2 rounded-xl bg-white/80 p-3">
            <ThunderboltOutlined className="mt-0.5 shrink-0 text-amber-500" />
            <p className="text-sm text-slate-700">
              每成功邀请 1 位好友注册，获得
              <span className="mx-1 font-extrabold bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">
                {window.config?.rewardPoints ?? 0}
              </span>
              积分奖励
            </p>
          </div>

          <div className="space-y-3">
            <Input disabled value={DefaultInviteUrl} className="text-sm" />
            <Button
              type="primary"
              size="large"
              icon={<CopyOutlined />}
              onClick={handleCopyLink}
              className="h-12 w-full rounded-xl font-medium transition-all duration-150 hover:brightness-105"
              style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                borderColor: 'transparent',
              }}
            >
              复制分享链接
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointsGetter;
